import { useEffect, useState } from "react";
import { TextField, Autocomplete, CircularProgress } from "@mui/material";
import patientService from "../../../../services/patient";
import useDebounce from "../../../../hooks/useDebounce";
import { userStatus } from "../../../../constants/user";

interface ReferralSearchDropdownProps {
  title: string;
  onPatientSelect: (patientId: string) => void;
}

const ReferralSearchDropdown = ({
  title,
  onPatientSelect,
}: ReferralSearchDropdownProps) => {
  const [options, setOptions] = useState<Array<{ id: string; label: string }>>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [debouncedValue, inputValue, setInputValue] = useDebounce<string>(
    "",
    500
  );

  const searchPatients = async (searchText: string) => {
    if (!searchText || searchText.length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const filtrationInfo = {
        approvedBy: [],
        createdBy: [],
        district: "",
        endDate: "",
        keyword: searchText,
        startDate: "",
        status: [userStatus.DISCHARGED, userStatus.CLOSED, userStatus.LAMA],
      };

      const response = await patientService.search(0, 100, filtrationInfo);
      const patients = response.data.data.map((patient: any) => ({
        id: patient._id,
        label: `${patient.ID} (${patient.name})`,
      }));
      setOptions(patients);
    } catch (error) {
      console.error("Error searching patients:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchPatients(debouncedValue);
  }, [debouncedValue]);

  return (
    <>
      <h5
        style={{
          fontFamily: "Montserrat",
          fontStyle: "normal",
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "20px",
          color: "#242424",
          margin: "10px 0",
        }}
      >
        {title}
      </h5>

      <Autocomplete
        fullWidth
        options={options}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, value) => {
          if (value) {
            onPatientSelect(value.id);
          } else {
            onPatientSelect("");
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Patient ID"
            variant="filled"
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          inputValue.length < 3
            ? "Type at least 3 characters to search"
            : "No options"
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    </>
  );
};

export default ReferralSearchDropdown;
